import { createReducer, PayloadAction } from '@reduxjs/toolkit';
import { getVacancyRequestsThunk } from '../vacancies/vacancies.thunk';
import {
  clearRequestsOptionsAction,
  clearRequestsAction,
  clearCurrentRequestsAction,
  clearLinkedCandidatesByRequestAction,
  setTabToOpenAction,
  clearLoggerDataAction,
} from './requests.actions';
import { preloadedRequestsState } from './requests.preloaded-state';
import {
  getRequestByIdThunk,
  createRequestThunk,
  updateRequestThunk,
  getAllRequestsThunk,
  exportRequestsThunk,
  deleteRequestThunk,
  getRequestLoggerDataThunk,
  getRequestCandidatesThunk,
  updateRequestCandidateDecisionThunk,
  detachRequestCandidateThunk,
  closeRequestThunk,
  updateRequestExecutorsThunk,
  updateRequestPriorityBoostThunk,
  updateRequestPriorityThunk,
} from './requests.thunk';
import { RequestItemResponse, RequestsState } from './requests.types';
import { transformArrayToObject, getArrayIds } from '@innowise-group/utilities';
import { logoutAction } from '../common';

// The request in a response comes without the pre-screening candidates the card has already loaded
const keepLinkedCandidates = (request: RequestItemResponse | null, payload: RequestItemResponse) => ({
  ...payload,
  linkedCandidatesIsLoading: request?.linkedCandidatesIsLoading ?? false,
  linkedCandidatesIds: request?.linkedCandidatesIds ?? [],
  linkedCandidatesItems: request?.linkedCandidatesItems ?? {},
});

// Every request action answers with the whole request, so the card and the list row take it as it is. A late answer
// must not replace another card opened in the meantime, so the card takes only its own request
const updateCurrentRequest = (state: RequestsState, { payload }: PayloadAction<RequestItemResponse>) => {
  if (state.requests.currentRequest?.id === payload.id) {
    state.requests.currentRequest = keepLinkedCandidates(state.requests.currentRequest, payload);
  }
  if (state.requests.items[payload.id]) {
    state.requests.items[payload.id] = keepLinkedCandidates(state.requests.items[payload.id], payload);
  }
};

// The candidates of a request live in the card and in its list row. An answer about a card that was left in the meantime
// does not go into the next one
const updateRequestCandidates = (
  state: RequestsState,
  requestId: number,
  update: (request: RequestItemResponse) => void,
) => {
  if (state.requests.currentRequest?.id === requestId) update(state.requests.currentRequest);
  if (state.requests.items[requestId]) update(state.requests.items[requestId]);
};

export const requestsReducer = createReducer(preloadedRequestsState, (builder) =>
  builder
    .addCase(createRequestThunk.fulfilled, (state) => {
      state.requests.isLoading = false;
    })
    .addCase(createRequestThunk.pending, (state) => {
      state.requests.isLoading = true;
    })
    .addCase(createRequestThunk.rejected, (state) => {
      state.requests.isLoading = false;
    })
    .addCase(deleteRequestThunk.fulfilled, (state) => {
      state.requests.isLoading = false;
      state.requests.currentRequest = null;
    })
    .addCase(deleteRequestThunk.pending, (state) => {
      state.requests.isLoading = true;
    })
    .addCase(deleteRequestThunk.rejected, (state) => {
      state.requests.isLoading = false;
    })
    .addCase(updateRequestExecutorsThunk.fulfilled, updateCurrentRequest)
    .addCase(updateRequestPriorityBoostThunk.fulfilled, updateCurrentRequest)
    .addCase(updateRequestPriorityThunk.fulfilled, updateCurrentRequest)
    .addCase(updateRequestPriorityThunk.rejected, (state, { type, payload }) => {
      // The base was saved, only the boost failed
      if (payload?.request) updateCurrentRequest(state, { type, payload: payload.request });
    })
    .addCase(closeRequestThunk.fulfilled, updateCurrentRequest)
    // TODO: Normalize implementation <<<BEGIN
    // https://innowise-group.atlassian.net/jira/software/projects/ITSRT/boards/1455?selectedIssue=ITSRT-504&text=504
    // US-6.2:
    // АС-7
    .addCase(setTabToOpenAction, (state, { payload }) => {
      state.tabToOpen = payload;
    })
    .addCase(updateRequestThunk.fulfilled, (state, action) => {
      updateCurrentRequest(state, action);
      state.requests.isLoading = false;
    })
    .addCase(updateRequestThunk.pending, (state) => {
      state.requests.isLoading = true;
    })
    .addCase(updateRequestThunk.rejected, (state) => {
      state.requests.isLoading = false;
    })
    .addCase(clearRequestsOptionsAction, (state) => {
      state.selectOptions = preloadedRequestsState.selectOptions;
    })

    .addCase(getRequestLoggerDataThunk.fulfilled, (state, { payload, meta }) => {
      if (meta.arg.params.page === 1) {
        state.logger.items = transformArrayToObject(payload.content);
        state.logger.ids = getArrayIds(payload.content);
      } else {
        state.logger.items = { ...state.logger.items, ...transformArrayToObject(payload.content) };
        state.logger.ids = [...new Set([...state.logger.ids, ...getArrayIds(payload.content)])];
      }
      state.logger.isLoading = false;
      state.logger.totalPages = payload.totalPages;
      state.logger.totalElements = payload.totalElements;
    })
    .addCase(getRequestLoggerDataThunk.pending, (state) => {
      state.logger.isLoading = true;
    })
    .addCase(getRequestLoggerDataThunk.rejected, (state) => {
      state.logger = preloadedRequestsState.logger;
    })

    .addCase(getVacancyRequestsThunk.fulfilled, (state, { payload, meta }) => {
      if (meta.arg.page === 1) {
        state.requests.items = transformArrayToObject(payload.items);
        state.requests.ids = getArrayIds(payload.items);
      } else {
        state.requests.items = { ...state.requests.items, ...transformArrayToObject(payload.items) };
        state.requests.ids = [...new Set([...state.requests.ids, ...getArrayIds(payload.items)])];
      }
      state.requests.isLoading = false;
      state.requests.totalPages = payload.totalPages;
    })
    .addCase(getVacancyRequestsThunk.pending, (state) => {
      state.requests.isLoading = true;
    })
    .addCase(getVacancyRequestsThunk.rejected, (state) => {
      state.requests = preloadedRequestsState.requests;
    })
    .addCase(getRequestByIdThunk.fulfilled, (state, { payload }) => {
      state.requests.ids = getArrayIds([payload]);
      state.requests.items = transformArrayToObject([payload]);
      state.requests.currentRequest = payload;
      state.requests.isLoading = false;
    })
    .addCase(getRequestByIdThunk.pending, (state) => {
      state.requests.isLoading = true;
    })
    .addCase(getRequestByIdThunk.rejected, (state) => {
      state.requests = preloadedRequestsState.requests;
    })
    .addCase(clearRequestsAction, (state) => {
      state.requests = preloadedRequestsState.requests;
    })
    .addCase(clearCurrentRequestsAction, (state) => {
      state.requests.currentRequest = null;
    })
    .addCase(getAllRequestsThunk.fulfilled, (state, { payload }) => {
      state.requests.items = transformArrayToObject(payload.content);
      state.requests.ids = getArrayIds(payload.content);
      state.requests.isLoading = false;
      state.requests.totalElements = payload.totalElements;
      state.requests.totalPages = payload.totalPages;
    })
    .addCase(getAllRequestsThunk.pending, (state) => {
      state.requests.isLoading = true;
    })
    .addCase(getAllRequestsThunk.rejected, (state) => {
      state.requests = preloadedRequestsState.requests;
    })
    .addCase(exportRequestsThunk.fulfilled, (state) => {
      state.requests.isExportLoading = false;
    })
    .addCase(exportRequestsThunk.pending, (state) => {
      state.requests.isExportLoading = true;
    })
    .addCase(exportRequestsThunk.rejected, (state) => {
      state.requests.isExportLoading = false;
    })
    .addCase(getRequestCandidatesThunk.fulfilled, (state, { payload, meta }) => {
      updateRequestCandidates(state, meta.arg.id, (request) => {
        request.linkedCandidatesIsLoading = false;
        request.linkedCandidatesIds = getArrayIds(payload);
        request.linkedCandidatesItems = transformArrayToObject(payload);
      });
    })
    .addCase(getRequestCandidatesThunk.pending, (state, { meta }) => {
      updateRequestCandidates(state, meta.arg.id, (request) => {
        request.linkedCandidatesIsLoading = true;
      });
    })
    .addCase(getRequestCandidatesThunk.rejected, (state, { meta }) => {
      updateRequestCandidates(state, meta.arg.id, (request) => {
        request.linkedCandidatesIsLoading = false;
      });
    })
    .addCase(clearLinkedCandidatesByRequestAction, (state, { payload }) => {
      if (state.requests.items[payload]) {
        state.requests.items[payload].linkedCandidatesIds = [];
        state.requests.items[payload].linkedCandidatesItems = {};
      }
    })
    // The answer is the whole link with the new decision, its author and date
    .addCase(updateRequestCandidateDecisionThunk.fulfilled, (state, { payload, meta }) => {
      updateRequestCandidates(state, meta.arg.id, (request) => {
        if (request.linkedCandidatesItems?.[payload.id]) request.linkedCandidatesItems[payload.id] = payload;
      });
    })
    .addCase(detachRequestCandidateThunk.fulfilled, (state, { meta }) => {
      state.requests.isLoading = false;
      updateRequestCandidates(state, meta.arg.id, (request) => {
        request.linkedCandidatesIds = (request.linkedCandidatesIds || []).filter((linkId) => {
          const isDetached = request.linkedCandidatesItems?.[linkId]?.candidate.id === meta.arg.candidateId;
          if (isDetached) delete request.linkedCandidatesItems[linkId];
          return !isDetached;
        });
      });
    })
    .addCase(detachRequestCandidateThunk.pending, (state) => {
      state.requests.isLoading = true;
    })
    .addCase(detachRequestCandidateThunk.rejected, (state) => {
      state.requests.isLoading = false;
    })
    .addCase(clearLoggerDataAction, (state) => {
      state.logger = preloadedRequestsState.logger;
    })
    .addCase(logoutAction, () => preloadedRequestsState),
);
